import React, { ChangeEvent, Component } from "react";
import filesize from "filesize";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { ICloudFile } from "@formant/core/model/ICloudFile";
import { IUser } from "@formant/core/model/IUser";

import { appStore, cloudFileStore, userStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import { CopyCode } from "../../common/CopyCode";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";
import * as styles from "./index.scss";

@observer
export class CloudFiles extends Component {
    private fileRef: React.RefObject<HTMLInputElement> = React.createRef();

    @localSettingObservable("settings-cloudfiles-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-cloudfiles-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    @observable
    private uploading = false;

    // eslint-disable-next-line react/require-render-return
    public render() {
        const cloudFiles: ICloudFile[] = cloudFileStore.listAll() ?? [];
        const users: IUser[] = userStore.listAll() || [];

        const columns: IDataTableColumn<ICloudFile>[] = [
            {
                header: "File Name",
                row: _ => <RowTitle title={_.name} icon="file" />,
                sortBy: _ => _.name,
            },
            {
                header: "Size",
                row: _ => filesize(_.fileSize),
                sortBy: _ => _.fileSize.toString().padStart(20, "0"),
            },
            {
                header: "User",
                row: cloudFile => {
                    const user = users.find(_ => _.id === cloudFile.userId);
                    return `${user?.firstName} ${user?.lastName}`.trim();
                },
                sortBy: cloudFile => {
                    const user = users.find(_ => _.id === cloudFile.userId);
                    return `${user?.firstName} ${user?.lastName}`.trim();
                },
            },
            {
                header: "Uploaded",
                row: _ => <RowDate date={_.createdAt} showTime={true} />,
                sortBy: _ => _.createdAt,
                hideBelow: "lg",
            },
            {
                header: "File ID",
                row: _ => <CopyCode value={_.fileId || ""} singleLine={true} />,
                className: styles.fileid,
            },
            {
                header: "",
                row: _ => <Actions cloudFile={_} />,
            },
        ];

        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions: {
            dimensionKey: string;
            dimensionName: string;
            getValue: (_: ICloudFile) => any;
        }[] = [];

        const filtered = applySimpleFilter(
            cloudFiles || [],
            dimensions,
            _ => _.name,
            filter,
        );

        return (
            <SettingsPage
                title="Cloud Files"
                className={styles.cloudfiles}
                extra={
                    <Help
                        text="Files uploaded to Formant cloud storage"
                        to="https://docs.formant.io/docs/uploading-files-to-formant-cloud"
                    />
                }
                actions={
                    <>
                        <StandardButton
                            onClick={this.onUpload}
                            disabled={this.uploading}
                            loading={this.uploading}
                            onChange={this.onUploadFileSelected}
                        >
                            Upload file
                        </StandardButton>
                        <input
                            type="file"
                            style={{ display: "none" }}
                            ref={this.fileRef}
                            onChange={this.onUploadFileSelected}
                        />
                    </>
                }
            >
                <StatsHeader
                    icon="file"
                    loading={!cloudFiles}
                    totalQuantity={cloudFiles?.length || 0}
                    totalLabel="Cloud files"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={cloudFiles || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filtered || []}
                        columns={columns}
                        loading={cloudFiles === undefined}
                        noResultsText="No cloud files found"
                        className={styles.table}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onUpload = () => {
        if (this.fileRef.current) {
            this.fileRef.current.click();
        }
    };

    private onUploadFileSelected = async (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            if (!file?.size) {
                appStore.setSnackbar(
                    `Upload error. File '${file.name}' is empty`,
                );
                return;
            }

            if (
                cloudFileStore.listAll()?.find(f => f.name === file.name)?.name
            ) {
                appStore.setConfirm({
                    title: "Upload file",
                    message: `There's already a file named "${file.name}". Do you want to replace it with the one you're uploading?`,
                    onOk: async () => {
                        this.uploading = true;
                        await cloudFileStore.uploadFile(file, {
                            forceOverwrite: true,
                        });
                        this.uploading = false;
                        // Clear current selection to allow selecting the same file again
                        event.target.value = "";
                    },
                    okCopy: "Replace file",
                    cancelCopy: "Cancel",
                    critical: true,
                });
            } else {
                this.uploading = true;
                await cloudFileStore.uploadFile(file);
            }
        } catch (error) {
            const responseError = narrowError(error, ResponseError);

            Object.values(responseError.validationErrors).forEach(message =>
                appStore.setSnackbar(message),
            );
        } finally {
            this.uploading = false;
            event.target.value = "";
        }
    };
}
